import { useEffect } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import api from '../../lib/api';
import { useAuthStore } from '../../stores/auth-store';
import {
  clearImpersonatorSession,
  getImpersonatorSession,
} from '../../lib/impersonation';
import { withAdminRefreshSettled } from '../../lib/token-refresh';
import { User } from '../../stores/auth-store';

interface LoginRequest {
  email: string;
  password: string;
  // ATL-001057 — reCAPTCHA v3 token (UAT/Prod only; omitted in dev/demo).
  captchaToken?: string;
  // ATL-001176 — 'v2' when re-submitting after the v2 checkbox fallback.
  captchaVersion?: 'v2' | 'v3';
}

interface ApiResponse<T> {
  success: boolean;
  data: T;
}

interface LoginData {
  user: User;
  tokens: {
    accessToken: string;
    refreshToken: string;
  };
}

export function useLogin() {
  const login = useAuthStore((state) => state.login);

  return useMutation({
    mutationFn: async (data: LoginRequest): Promise<LoginData> => {
      const response = await api.post<ApiResponse<LoginData>>('/auth/login', data);
      return response.data.data;
    },
    onSuccess: (data) => {
      localStorage.setItem('refreshToken', data.tokens.refreshToken);
      login(data.user, data.tokens.accessToken);
    },
  });
}

export function useLogout() {
  const logout = useAuthStore((state) => state.logout);

  return useMutation({
    mutationFn: async () => {
      try {
        // ATL-001145 (SEC-036): send the refresh token the login flow stored.
        // POST /auth/logout reads it from the BODY (the httpOnly cookie is
        // vestigial — it is only set in production and the controller never
        // reads it), and it is what identifies the session to revoke. Without
        // it the server had nothing to act on: it cleared a cookie nobody uses
        // and left both the refresh row and the (then 24h) access token alive, so
        // "Sign Out" only ever cleared this browser's localStorage.
        //
        // While impersonating, `enterAppImpersonation` has REMOVED the refresh
        // token from localStorage and stashed the operator's own session in
        // sessionStorage. The session actually being ended is the operator's,
        // so revoke with their stashed refresh token and drop the stash —
        // otherwise their tokens outlive their sign-out on a shared device.
        // Never revoke with the impersonated user's credentials: bumping their
        // tokenVersion would sign that real user out of their own devices.
        //
        // ATL-001144: tokens are renewed silently, so read the refresh token
        // only once no renewal is in flight (in any tab) and keep renewals out
        // until the revoke is done — otherwise we could revoke a token that is
        // being rotated away (revoking nothing) while its successor lands in
        // storage. It is dropped locally first so nothing can renew with it.
        await withAdminRefreshSettled(async () => {
          const impersonator = getImpersonatorSession();
          const refreshToken =
            impersonator?.admin.refreshToken ?? localStorage.getItem('refreshToken');
          localStorage.removeItem('refreshToken');
          await api.post('/auth/logout', refreshToken ? { refreshToken } : {});
        });
      } catch {
        // Logout locally even if API call fails
      } finally {
        // Runs even when the request throws — a failed revoke must not leave
        // the operator's stashed tokens sitting in sessionStorage.
        clearImpersonatorSession();
      }
    },
    onSuccess: () => {
      logout();
    },
  });
}

export function useMe() {
  const setUser = useAuthStore((state) => state.setUser);
  const setIsLoading = useAuthStore((state) => state.setIsLoading);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  const query = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: async (): Promise<User> => {
      const response = await api.get<ApiResponse<{ user: User }>>('/auth/me');
      return response.data.data.user;
    },
    enabled: isAuthenticated,
  });

  useEffect(() => {
    if (query.status === 'success' && query.data) {
      setUser(query.data);
      setIsLoading(false);
    } else if (query.status === 'error') {
      setUser(null);
      setIsLoading(false);
    }
  }, [query.status, query.data, setUser, setIsLoading]);

  return query;
}
